import React, { useRef, useState } from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '@/src/utils';

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => {
  const scrollXRef = useRef(0);

  const innerRef = useRef<HTMLDivElement>(null);
  const refOrigin = ref || innerRef;

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (e.deltaX) {
      return;
    }

    if ('current' in refOrigin && refOrigin.current) {
      let moveX = (scrollXRef.current || 0) + e.deltaY;
      const scrollWidth =
        refOrigin.current.scrollWidth - refOrigin.current?.offsetWidth;

      if (moveX < 0) {
        moveX = 0;
      }

      if (scrollWidth && moveX > scrollWidth) {
        moveX = scrollWidth;
      }

      scrollXRef.current = moveX;

      refOrigin.current.scroll({ left: moveX });
    }
  };

  return (
    <TabsPrimitive.List
      ref={refOrigin}
      className={cn(
        'inline-flex h-10 items-center justify-center border-b-[1px] p-1',
        className,
      )}
      onWheel={handleWheel}
      {...props}
    />
  );
});
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & {
    isSelected?: boolean;
  }
>(({ className, isSelected, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      'inline-flex h-[40px] min-w-[170px] items-center justify-center whitespace-nowrap border-b-[3px] border-transparent px-3 text-b16m outline-transparent transition-all selection:outline-none  disabled:pointer-events-none disabled:opacity-50 data-[state=active]:border-b-[3px] data-[state=active]:border-gray9 data-[state=active]:text-foreground data-[state=active]:before:hidden data-[state=active]:after:hidden data-[state=active]:focus:outline-none',
      `${isSelected && 'border-gray9 text-foreground'}`,
      className,
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn('mt-2 focus-visible:outline-none ', className)}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
